import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useAuth } from "@/contexts/auth-context";
import { useBrand } from "@/contexts/brand-context";
import { useSubscription } from "@/contexts/subscription-context";
import { toast } from "@/components/ui/use-toast";
import { LoadingScreen } from "@/components/LoadingScreen";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const { isLoading: isBrandsLoading } = useBrand();
  const { isLoading: isSubscriptionLoading } = useSubscription();
  const navigate = useNavigate();

  // Handle redirection after authentication and data loading
  useEffect(() => {
    // Only proceed with navigation when authentication is confirmed AND both brands and subscription data are fully loaded
    if (isAuthenticated && !isBrandsLoading && !isSubscriptionLoading) {
      setIsAuthenticating(false);
      navigate("/");
    }
  }, [isAuthenticated, isBrandsLoading, isSubscriptionLoading, navigate]);

  const handleError = (error: string) => {
    toast({
      title: "Error",
      description: error,
      variant: "destructive",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsAuthenticating(true);

    try {
      await login(email, password);
      // Navigation is handled by the useEffect above
    } catch (error) {
      console.error("Login error:", error);
      setIsAuthenticating(false);
      handleError(error instanceof Error ? error.message : "Failed to sign in");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sidebar">
      {isAuthenticating && <LoadingScreen />}
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Creator Logo"
            className="h-12 w-auto mx-auto mb-4"
          />
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>Sign In</CardTitle>
            <CardDescription>
              Enter your credentials to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <GoogleSignInButton onError={handleError} />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with email
                  </span>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    autoFocus
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      tabIndex={-1}
                      to="/forgot-password"
                      className="text-sm text-stone-500 hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Don't have an account?{" "}
                  <Link to="/signup" className="text-primary hover:underline">
                    Sign up
                  </Link>
                </p>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignIn;
